<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useConfirm } from 'primevue/useconfirm'
import { FilterMatchMode } from '@primevue/core/api'

// PrimeVue components
import { default as PButton } from 'primevue/button'
import { default as PDataTable } from 'primevue/datatable'
import { default as PColumn } from 'primevue/column'
import { default as PInputText } from 'primevue/inputtext'
import { default as PIconField } from 'primevue/iconfield'
import { default as PInputIcon } from 'primevue/inputicon'
import { default as PButtonGroup } from 'primevue/buttongroup'
import { default as PTag } from 'primevue/tag'
import { default as PSkeleton } from 'primevue/skeleton'
import { default as PMessage } from 'primevue/message'

// Custom components
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import CLearningCourseHeader from '@/presentation/components/learning/course/CLearningCourseHeader.vue'

// Stores
import { useLearningCoursesStore } from '@/presentation/stores/learning/learningCourses'
import { useReferentialStore } from '@/presentation/stores/referential/referential'

// Domain models & types
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus'
import { LearningCourse } from '@/core/domain/learning/course/LearningCourse'
import { useLearningCourseAccessUtils } from '@/presentation/composables/learning/useLearningAccessUtils.ts'

// SETUP
const router = useRouter()
const confirm = useConfirm()

// STORES
const learningCoursesStore = useLearningCoursesStore()
const referentialStore = useReferentialStore()

// STORE REFS - destructured properties with storeToRefs for reactivity
const { learningCourses, loading, error, lastUpdated } = storeToRefs(learningCoursesStore)
const {canEditCourse, canDeleteCourse} = useLearningCourseAccessUtils()

// COMPUTED
const isLoading = computed(() => loading.value.fetchAllCourses)
const isEmpty = computed(() => !isLoading.value && learningCourses.value.length === 0)
const hasError = computed(() => error.value && error.value.hasError)
const errorMessage = computed(() => (error.value?.hasError ? error.value.message : ''))

// LOCAL STATE
const filters = ref({
  global: { value: '', matchMode: FilterMatchMode.CONTAINS }
})

// LIFECYCLE HOOKS
onMounted(async () => {
  try {
    // Fetch referential data
    await referentialStore.fetchAllAxes()
    // Fetch courses
    await learningCoursesStore.fetchAllCourses({
      withModules: true,
      withDomains: true,
      status: [LearningStatus.Values.ACTIVE, LearningStatus.Values.DRAFT]
    })
  } catch (error) {
    console.error('Erreur lors du chargement initial des données:', error)
  }
})

// METHODS
function createLearningCourse() {
  router.push({ name: 'learning-courses-add' })
}

function editCourse(courseId: number) {
  router.push({ name: 'learning-course-update', params: { courseId } })
}

function resetFilters() {
  for (const key in filters.value) {
    filters.value[key].value = null
  }
}

async function archiveCourse(id: number) {
  confirm.require({
    message: 'Êtes-vous sûr de vouloir archiver ce parcours ?',
    header: "Confirmation d'archivage",
    icon: 'pi pi-inbox',
    acceptLabel: 'Oui, archiver',
    rejectLabel: 'Annuler',
    acceptIcon: 'pi pi-check',
    rejectIcon: 'pi pi-times',
    accept: async () => {
      try {
        await learningCoursesStore.updateLearningCourseStatus(id, LearningStatus.Values.ARCHIVED)
      } catch (error) {
        console.error("Erreur lors de l'archivage du cours:", error)
      }
    }
  })
}

// Helper function to get domain title
function getDomainTitle(course: LearningCourse): string {
  return course.domain?.title || 'Non défini'
}

// Helper function to get axis title
function getAxisTitle(course: LearningCourse): string {
  if (!course.domain) return 'Non défini'
  return referentialStore.getAxisTitleFromDomainId(course.domain.id)
}
</script>

<template>
  <c-learning-course-header
    title="Tous les parcours"
    :primary-button="{
      label: 'Créer un parcours',
      onClick: createLearningCourse,
      icon: 'pi pi-plus'
    }"
  />

  <div class="col-span-12 flex-1 gap-8 overflow-y-auto p-8 pt-2">
    <!-- Message d'erreur -->
    <p-message v-if="hasError" severity="error" class="mb-4 w-full">
      {{ errorMessage }}
    </p-message>

    <!-- Bloc de contenu principal -->
    <c-base-block text>
      <!-- État de chargement -->
      <div v-if="isLoading" class="flex flex-col gap-2">
        <p-skeleton v-for="i in 3" :key="i" class="mb-2" height="3rem" />
      </div>

      <!-- État vide -->
      <div v-else-if="isEmpty" class="flex flex-col items-center justify-center p-8">
        <i class="pi pi-book text-surface-500 dark:text-surface-300 mb-4 text-4xl"></i>
        <h3 class="mt-0 mb-2">Aucun parcours</h3>
        <p class="text-surface-700 dark:text-surface-100 text-center">
          Vous n'avez pas encore créé de parcours.
        </p>
        <p-button
          label="Créer un parcours"
          icon="pi pi-plus"
          @click="createLearningCourse"
          class="mt-4"
        />
      </div>

      <!-- Tableau des parcours -->
      <p-data-table
        v-else
        :value="learningCourses"
        :filters="filters"
        :globalFilterFields="['title', 'name', 'domain.title']"
        dataKey="id"
        :paginator="learningCourses.length > 10"
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        responsiveLayout="scroll"
      >
        <!-- En-tête du tableau avec filtres -->
        <template #header>
          <div class="flex justify-between">
            <p-button
              label="Effacer les filtres"
              icon="pi pi-filter-slash"
              size="small"
              @click="resetFilters"
              outlined
            />
            <p-icon-field iconPosition="left">
              <p-input-icon class="pi pi-search"></p-input-icon>
              <p-input-text
                v-model="filters['global'].value"
                size="small"
                placeholder="Rechercher"
              />
            </p-icon-field>
          </div>
        </template>

        <!-- Messages d'état -->
        <template #empty>
          <p>Aucun parcours ne correspond à votre recherche.</p>
        </template>
        <template #loading>
          <p>Chargement des données...</p>
        </template>

        <!-- Colonnes -->
        <p-column header="Nom">
          <template #body="{ data }: { data: LearningCourse }">
            <p>{{ data.title }}</p>
          </template>
        </p-column>

        <p-column header="Domaine associé">
          <template #body="{ data }">
            <p>{{ getDomainTitle(data) }}</p>
          </template>
        </p-column>

        <p-column header="Axe">
          <template #body="{ data }">
            <p>{{ getAxisTitle(data) }}</p>
          </template>
        </p-column>

        <p-column header="Modules">
          <template #body="{ data }">
            <span>{{ data.learningModules.length }}</span>
          </template>
        </p-column>

        <p-column header="État" style="width: 8rem">
          <template #body="{ data }">
            <p-tag
              :value="LearningStatus.label(data.status)"
              :severity="LearningStatus.color(data.status)"
            />
          </template>
        </p-column>

        <p-column header="Actions" style="width: 8rem">
          <template #body="{ data }">
            <p-button-group>
              <p-button
                :icon="canEditCourse(data) ? 'pi pi-pencil' : 'pi pi-eye'"
                severity="secondary"
                text
                class="mr-2"
                @click="() => editCourse(data.id)"
                v-tooltip.top="canEditCourse(data) ? 'Modifier' : 'Visualiser'"
              />
              <p-button
                icon="pi pi-inbox"
                severity="secondary"
                :disabled="!canDeleteCourse(data)"
                text
                class="mr-2"
                @click="() => archiveCourse(data.id)"
                v-tooltip.top="'Archiver'"
              />
            </p-button-group>
          </template>
        </p-column>

        <template #footer>
          <p class="font-bold">Total: {{ learningCourses.length }} parcours</p>
        </template>
      </p-data-table>

      <!-- Dernière mise à jour -->
      <div v-if="lastUpdated" class="text-surface-500 mt-4 text-right text-xs">
        Dernière mise à jour : {{ new Date(lastUpdated).toLocaleString() }}
      </div>
    </c-base-block>
  </div>
</template>
